import { TreeNodeData } from "@tenforce/product-fields"
import { dataAPI } from "../api"
import {
    filterQueryMapper,
    orderQueryMapper,
    selectorModelMapper,
    typeQueryMapper,
} from "./utils"
import {
    ODataGetArrayResponse,
    ODataGetResourceResponse,
} from "../models/api/data"
import { TypeModel, Type } from "../models/treeSelector/models"
import { SelectableValue, ValueWithGroup } from "./type"
import { isFieldItemSelect } from "../../components/fields/components/treeSelector/utils"

export const parseGroupValueData = <T extends Type>(
    response:
        | ODataGetArrayResponse<TypeModel[T]>
        | ODataGetResourceResponse<TypeModel[T]>,
    type: T,
): ValueWithGroup[] => {
    if (!("value" in response) && response.Id === undefined) {
        return []
    }
    const responseChildren = "value" in response ? response.value : [response]

    const mapper = selectorModelMapper(type)
    return responseChildren.map((child) => mapper(child))
}

interface SelectableOptionWithGroup {
    data: ValueWithGroup[]
    totalChildrenCount: number
}

// values with decoration and other values
export const getChildren = async <T extends Type>(
    itemId: number,
    fieldId: string,
    type: T,
    filter = "",
    skip = 0,
    top = 20,
    ids: string[] = [],
): Promise<SelectableOptionWithGroup> => {
    let filterQuery = filterQueryMapper(filter, type, true)

    if (ids.length > 0) {
        let filterIdsString = ""

        ids.forEach((id, index) => {
            if (index > 0) {
                filterIdsString += " or"
            }
            filterIdsString += ` (id eq ${id})`
        })

        filterQuery = filterQuery
            ? `(${filterQuery})` + ` and (${filterIdsString})`
            : filterIdsString
    }

    const orderBy = orderQueryMapper(type)

    const parameters: Record<string, string | number | boolean | undefined> = {
        $filter: !!filterQuery ? `(${filterQuery})` : undefined,
        $count: true,
    }

    if (orderBy) {
        parameters["$orderby"] = orderBy
    }

    // If looking for selected values, we don't paginate.
    if (ids.length === 0) {
        parameters["$top"] = top
        parameters["$skip"] = skip
    }

    return dataAPI
        .getJSON<
            | ODataGetArrayResponse<TypeModel[T]>
            | ODataGetResourceResponse<TypeModel[T]>
        >(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${type}/SelectableValues`,
            parameters,
        )
        .then((response) => {
            return {
                data: parseGroupValueData(response, type),
                totalChildrenCount: response["@count"] ?? 0,
            }
        })
}

// values with decoration and other values
export const getSelectedChildren = async <T extends Type>(
    type: T,
    filter = "",
    ids: string[] = [],
): Promise<SelectableOptionWithGroup> => {
    let filterQuery = filterQueryMapper(filter, type, true)

    if (ids.length > 0) {
        let filterIdsString = ""
        ids.forEach((id, index) => {
            if (index > 0) {
                filterIdsString += ","
            }
            filterIdsString += id
        })
        filterQuery = filterQuery
            ? `(${filterQuery})` + ` and (id in (${filterIdsString}))`
            : `id in (${filterIdsString})`
    }

    const orderBy = orderQueryMapper(type)

    const parameters: Record<string, string | number | boolean | undefined> = {
        $filter: !!filterQuery ? `(${filterQuery})` : undefined,
        $count: true,
    }

    if (orderBy) {
        parameters["$orderby"] = orderBy
    }

    const apiUrl = typeQueryMapper(type)

    return dataAPI
        .getJSON<
            | ODataGetArrayResponse<TypeModel[T]>
            | ODataGetResourceResponse<TypeModel[T]>
        >(apiUrl, parameters)
        .then((response) => {
            return {
                data: parseGroupValueData(response, type),
                totalChildrenCount: response["@count"] ?? 0,
            }
        })
}

interface SelectableOptionsResponse {
    data: TreeNodeData<SelectableValue>[] | null
    totalChildrenCount: number
}

export const parseValueResponse = <T extends Type>(
    response:
        | ODataGetArrayResponse<TypeModel[T]>
        | ODataGetResourceResponse<TypeModel[T]>,
    type: T,
): TreeNodeData<SelectableValue>[] | null => {
    if (!("value" in response) && response.Id === undefined) {
        return null
    }
    const responseChildren = "value" in response ? response.value : [response]

    const mapper = selectorModelMapper(type)
    const children = responseChildren.map((child) => {
        const mapped = mapper(child)

        return {
            id: mapped.id.toString(),
            data: {
                id: mapped.id,
                label: mapped.label || "",
                isActive: mapped.isActive ?? true,
            },
            label: mapped.label,
            childrenCount: 0,
            hasChildren: false,
            children: [],
            isSelectable: true,
        }
    })

    return children
}

//plain value without decorations
export const getValues = async <T extends Type>(
    itemId: number,
    fieldId: string,
    type: T,
    filter = "",
    skip = 0,
    top = 20,
    ids: string[] = [],
): Promise<SelectableOptionsResponse> => {
    let filterQuery = filterQueryMapper(filter, type, true)

    if (ids.length > 0) {
        let filterIdsString = ""

        ids.forEach((id, index) => {
            if (index > 0) {
                filterIdsString += " or"
            }
            filterIdsString += ` (id eq ${id})`
        })

        filterQuery = filterQuery
            ? `(${filterQuery})` + ` and (${filterIdsString})`
            : filterIdsString
    }

    const orderBy = orderQueryMapper(type)

    const parameters: Record<string, string | number | boolean | undefined> = {
        $filter: !!filterQuery ? `(${filterQuery})` : undefined,
        $top: top,
        $skip: skip,
        $count: true,
    }

    if (orderBy) {
        parameters["$orderby"] = orderBy
    }

    return dataAPI
        .getJSON<
            | ODataGetArrayResponse<TypeModel[T]>
            | ODataGetResourceResponse<TypeModel[T]>
        >(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${type}/SelectableValues`,
            parameters,
        )
        .then((response) => {
            return {
                data: parseValueResponse(response, type),
                totalChildrenCount: response["@count"] ?? 0,
            }
        })
}

//plain selected value without decorations
export const getSelectedValues = async <T extends Type>(
    type: T,
    filter = "",
    ids: string[] = [],
): Promise<SelectableOptionsResponse> => {
    let filterQuery = filterQueryMapper(filter, type, true)

    if (ids.length > 0) {
        let filterIdsString = ""
        ids.forEach((id, index) => {
            if (index > 0) {
                filterIdsString += ","
            }
            filterIdsString += id
        })
        filterQuery = filterQuery
            ? `(${filterQuery})` + ` and (id in (${filterIdsString}))`
            : `id in (${filterIdsString})`
    }

    const orderBy = orderQueryMapper(type)

    const parameters: Record<string, string | number | boolean | undefined> = {
        $filter: !!filterQuery ? `(${filterQuery})` : undefined,
        $count: true,
    }

    if (orderBy) {
        parameters["$orderby"] = orderBy
    }

    const apiUrl = typeQueryMapper(type)

    return dataAPI
        .getJSON<
            | ODataGetArrayResponse<TypeModel[T]>
            | ODataGetResourceResponse<TypeModel[T]>
        >(apiUrl, parameters)
        .then((response) => {
            return {
                data: parseValueResponse(response, type),
                totalChildrenCount: response["@count"] ?? 0,
            }
        })
}

export const parseSelectorData = <T extends Type>(
    response: TypeModel[T] | TypeModel[T][],
    type: T,
): ValueWithGroup[] => {
    if (!response) {
        return []
    }
    const responseChildren = Array.isArray(response) ? response : [response]
    const mapper = selectorModelMapper(type)
    const childrenToBe = responseChildren.map((child) => {
        const mapped = mapper(child)
        const mappedChild = {
            ...mapped,
        }
        return mappedChild
    })
    return childrenToBe
}

export const mapToGroupedTreeStructure = <T extends Type>(
    valueResponse: ValueWithGroup[],
    type: T,
    selectedIds: string[],
): TreeNodeData<SelectableValue>[] => {
    if (!valueResponse || valueResponse.length === 0) {
        return []
    }

    const groupsMap = new Map<string | number, TreeNodeData<SelectableValue>>()

    valueResponse.forEach((item) => {
        const groupId = item.groupData?.id ?? "root" // Default to 'root' if groupData missing
        const groupName = item.groupData?.label ?? ""

        if (groupId === "root") {
            groupsMap.set(`root-${item.id}`, {
                id: `${item.id}`,
                label: item.label,
                data: {
                    id: item.id,
                    label: item.label,
                    isActive: item.isActive ?? true,
                },
                isSelectable: true,
                isExpanded: true,
                isSelected: selectedIds.includes(`${item.id}`),
                hasChildren: false,
                childrenCount: 0,
                children: [],
            })
        } else {
            let groupNode = groupsMap.get(groupId)

            if (!groupNode) {
                // Create new group node
                groupNode = {
                    id: String(groupId),
                    label: groupName,
                    data: {
                        id: groupId,
                        label: groupName,
                        isActive: item.isActive ?? true,
                    } as SelectableValue,
                    isSelected: false,
                    isSelectable: isFieldItemSelect(type),
                    isExpanded: true,
                    hasChildren: true,
                    childrenCount: 1,
                    children: [],
                }
                groupsMap.set(groupId, groupNode)
            } else {
                groupNode.childrenCount = (groupNode.childrenCount ?? 0) + 1
            }

            // Add current item as child to the group
            if (groupNode.children) {
                groupNode.children.push({
                    id: String(item.id),
                    label: item.label,
                    data: {
                        id: item.id,
                        label: item.label,
                        isActive: item.isActive ?? true,
                    } as SelectableValue,
                    isSelected: selectedIds.includes(`${item.id}`),
                    isSelectable: true,
                    isExpanded: true,
                    hasChildren: false,
                    childrenCount: 0,
                    children: undefined,
                })
            }
        }
    })

    return Array.from(groupsMap.values())
}

//TODO: right now not in used we removed last group selection checkbox
// we can remove after a proper solution for last group(partially loaded) selection
export const getChildrenForGroup = async <T extends Type>(
    itemId: number,
    fieldId: string,
    type: T,
    id: string,
): Promise<SelectableOptionWithGroup> => {
    const parameters: Record<string, string | number | boolean | undefined> = {
        $filter: `group/id eq ${id}`,
        $count: true,
    }

    return dataAPI
        .getJSON<
            | ODataGetArrayResponse<TypeModel[T]>
            | ODataGetResourceResponse<TypeModel[T]>
        >(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${type}/SelectableValues`,
            parameters,
        )
        .then((response) => {
            return {
                data: parseGroupValueData(response, type),
                totalChildrenCount: response["@count"] ?? 0,
            }
        })
}
